import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getRouteConfig } from '../router/routes';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface BreadcrumbProps {
  className?: string;
  style?: React.CSSProperties;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ className = '', style = {} }) => {
  const location = useLocation();
  const routeConfig = getRouteConfig(location.pathname);
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://stimm.digital';

  // Keine Breadcrumbs für die Startseite anzeigen
  if (!routeConfig?.breadcrumbs || location.pathname === '/') {
    return null;
  }

  const breadcrumbs = routeConfig.breadcrumbs;

  return (
    <nav 
      className={`breadcrumb ${className}`}
      style={{
        padding: '1rem 0',
        fontSize: '0.9rem',
        color: 'var(--text-muted)',
        ...style
      }}
      aria-label="Breadcrumb"
    >
      <ol style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        listStyle: 'none',
        margin: 0,
        padding: 0,
        gap: '0.5rem'
      }}>
        {breadcrumbs.map((item: BreadcrumbItem, index: number) => {
          const isLast = index === breadcrumbs.length - 1;
          
          return (
            <li key={index} style={{ display: 'flex', alignItems: 'center' }}>
              {item.path && !isLast ? (
                <Link 
                  to={item.path}
                  style={{
                    color: 'var(--primary-color)',
                    textDecoration: 'none',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--primary-color-dark)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--primary-color)';
                  }}
                >
                  {item.label}
                </Link>
              ) : (
                <span style={{
                  color: isLast ? 'var(--text-color)' : 'var(--text-muted)',
                  fontWeight: isLast ? '500' : 'normal'
                }}>
                  {item.label}
                </span>
              )}
              
              {!isLast && (
                <span style={{
                  margin: '0 0.5rem',
                  color: 'var(--text-muted)',
                  fontSize: '0.8rem'
                }}>
                  <i className="fas fa-chevron-right"></i>
                </span>
              )}
            </li>
          );
        })}
      </ol>

      {/* Strukturierte Daten für SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbs.map((item: BreadcrumbItem, index: number) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": item.label,
              ...(item.path && {
                "item": `${origin}${item.path}`
              })
            }))
          })
        }}
      />
    </nav>
  );
};

export default Breadcrumb;
